import React, { useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Breadcrumb, Layout, PageHeader, Spin, Tag } from 'antd'

// Services
import { errorMessage, successMessage } from 'services/messager'
import {
	downloadAttachment,
	setResponsible,
	updateStatus,
} from 'services/demands/demandsServices'
import { useSelector } from 'react-redux'

// Components
import MainLayout from 'components/mainLayout'
import AttachmentPreview from 'components/attachment'
import InfoGrid from 'components/infoGrid'
import DemandsButtons from 'components/demandsButtons'
import ConfirmationModal from 'components/ConfirmationModal'
import SetResponsibleModal from 'pages/demands/components/SetResponsibleModal'

// Hooks
import useDemandAttachments from 'hooks/demands/useDemandAttachments'
import useDemandDetails from './hooks/useDemandDetails'

import styles from './index.module.scss'

const statusColorMap = {
	'Aguardando ação': 'processing',
	'Em Validação': 'geekblue',
	'Em Revisão': 'cyan',
	'Em Assinatura': 'purple',
	Concluído: 'success',
	Atrasado: 'error',
	Cancelado: 'orange',
	Arquivado: 'gold',
}

const priorityColorMap = {
	Baixa: 'geekblue',
	Média: 'warning',
	Alta: 'volcano',
	Urgente: 'error',
}

export default function DetailsDemands() {
	const { id } = useParams()
	const history = useHistory()
	const queryClient = useQueryClient()
	const user = useSelector(({ session }) => session)
	const { data, isLoading, isError } = useDemandDetails(id)
	const {
		data: attachments,
		isLoading: isLoadingAttachments,
		isError: isErrorAttachments,
	} = useDemandAttachments(id)
	const hasLegalAccess = ['legal', 'admin'].includes(user?.perfil)

	// Checks if user is from company AllCare to change label
	const taskNameLabel =
		user.company_id === 96
			? 'Título do chamado'
			: 'Nome do fornecedor/contraparte'

	const [openModal, setOpenModal] = useState(undefined) // cancel | finish

	const generalInfoItems = [
		{ value: data?.category?.name, label: 'Categoria' },
		{ value: data?.requester?.name, label: 'Solicitante' },
		{
			value: data?.solicitantArea?.name || 'Não informada',
			label: 'Área Solicitante',
		},
		...(data?.document_template_id
			? [
					{
						value: data?.model?.name,
						label: 'Modelo',
					},
				]
			: []),
		{ value: data?.requester?.email, label: 'E-mail do solicitante' },
	]

	const legalInfoItems = [
		{
			value: data?.responsible?.name,
			label: 'Responsável jurídico designado',
		},
		{
			value: new Date(data?.deadline).toLocaleDateString('pt-BR'),
			label: 'Prazo de conclusão',
		},
		...(data?.document_id
			? [
					{
						value: data?.name,
						label: 'Documento associado',
						isButton: true,
						onClick: () => {
							history.push(`/documents/${data.document_id}`)
						},
					},
				]
			: []),
	]

	const { mutate: handleDownloadAttachment, isPending: isDownloading } =
		useMutation({
			mutationFn: async (attachment) => {
				const data = await downloadAttachment(attachment.id)

				return data.fileUrl
			},
			onSuccess: (fileUrl) => {
				window.open(fileUrl, '_blank', 'noopener,noreferrer')
				successMessage({
					content: 'Download do anexo feito com sucesso!',
				})
			},
			onError: (error) => {
				console.log(error)
				errorMessage({
					content: 'Falha durante o download do anexo.',
				})
			},
		})

	const { mutate: changeDemandStatus, isPending: isLoadingChangeStatus } =
		useMutation({
			mutationFn: async ({ status }) => {
				const data = await updateStatus({ id, status })
				return data
			},
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ['demands'] })
				queryClient.invalidateQueries({ queryKey: ['demand', id] })
				setOpenModal(undefined)
				successMessage({
					content: 'Status da demanda alterado com sucesso!',
				})
			},
			onError: () => {
				errorMessage({
					content: 'Erro ao alterar status da demanda.',
				})
			},
		})

	const { mutate: handleSetResponsible, isPending: isLoadingSetResponsible } =
		useMutation({
			mutationFn: async (values) => {
				const data = await setResponsible({
					taskId: id,
					userId: values.responsible,
				})
				return data
			},
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ['demands'] })
				queryClient.invalidateQueries({ queryKey: ['demand', id] })
				setOpenModal(undefined)
				successMessage({ content: 'Responsável atribuido com sucesso!' })
			},
			onError: (error) => {
				console.log(error)
				errorMessage({
					content: 'Erro ao atribuir responsável.',
				})
			},
		})

	function handleFinishDemand() {
		changeDemandStatus({ status: 'Concluído' })
	}

	function handleCancelDemand() {
		changeDemandStatus({ status: 'Cancelado' })
	}

	function handleCloseModal() {
		setOpenModal(undefined)
	}

	function handleOpenModal(modal) {
		setOpenModal(modal)
	}

	const handleGoBack = () => {
		history.goBack()
	}

	if (isLoading) {
		return (
			<MainLayout>
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						height: '100%',
						width: '100%',
					}}>
					<Spin size="large" />
				</div>
			</MainLayout>
		)
	}

	if (isError) {
		return (
			<MainLayout>
				<div>
					Não foi possível carregar os dados. Tente novamente mais tarde.
				</div>
			</MainLayout>
		)
	}

	if (!data) {
		return (
			<MainLayout>
				<div>Demanda não encontrada. Retorne à lista de demandas.</div>
			</MainLayout>
		)
	}

	return (
		<MainLayout selectedKey="demands.list">
			<Layout style={{ backgroundColor: '#fff' }}>
				<PageHeader>
					<Breadcrumb>
						<Breadcrumb.Item
							onClick={handleGoBack}
							style={{ cursor: 'pointer' }}>
							Demandas
						</Breadcrumb.Item>
						<Breadcrumb.Item>{data.name}</Breadcrumb.Item>
					</Breadcrumb>
					<DemandsButtons
						id={id}
						onOpenModal={handleOpenModal}
						loading={isLoadingChangeStatus}
						hasResponsible={!!data.responsible}
					/>
				</PageHeader>
				<Layout style={{ backgroundColor: '#fff' }}>
					<ConfirmationModal
						show={openModal === 'cancel'}
						title={'Cancelar demanda'}
						text={'Deseja realmente cancelar esta demanda?'}
						onCancel={handleCloseModal}
						cancelText="Fechar"
						onConfirm={handleCancelDemand}
						loading={isLoadingChangeStatus}
					/>
					<ConfirmationModal
						show={openModal === 'finish'}
						title={'Concluir demanda'}
						text={'Deseja marcar a demanda como concluída?'}
						onCancel={handleCloseModal}
						onConfirm={handleFinishDemand}
						loading={isLoadingChangeStatus}
					/>
					<SetResponsibleModal
						show={openModal === 'set-responsible'}
						onCancel={handleCloseModal}
						onConfirm={handleSetResponsible}
						loading={isLoadingSetResponsible}
						isLegalUser={user?.perfil === 'legal'}
						teamId={data?.team_id}
					/>
					<div className={styles.container}>
						<header className={styles.header}>
							<div>
								<h1 className={styles.title}>{data.name}</h1>
								<p className={styles.subtitle}>{taskNameLabel}:</p>
							</div>
							<aside className={styles.asideContainer}>
								<div>
									<h4 className={styles.asideTitle}>Status</h4>
									<Tag color={statusColorMap[data.status] || 'gray'}>
										{data.status}
									</Tag>
								</div>
								<div>
									<h4 className={styles.asideTitle}>Prioridade</h4>
									<Tag color={priorityColorMap[data.priority] || 'gray'}>
										{data.priority}
									</Tag>
								</div>
							</aside>
						</header>

						<InfoGrid infoItems={generalInfoItems} />

						{hasLegalAccess && (
							<section className={styles.sectionContainer}>
								<p className={styles.sectionTitle}>Campos exclusivos:</p>
								<InfoGrid infoItems={legalInfoItems} />
							</section>
						)}

						<section className={styles.descriptionSection}>
							<h2>Descrição da demanda</h2>
							<div className={styles.descriptionBox}>
								<p>{data.description ?? 'Nenhuma descrição fornecida.'}</p>
							</div>
						</section>

						<section className={styles.attachmentSection}>
							<h2>Anexos</h2>
							<div style={{ maxWidth: '400px', marginTop: '10px' }}></div>
							<ul className={styles.attachmentList}>
								{isLoadingAttachments && <Spin />}
								{!isErrorAttachments &&
									attachments?.map((attachment) => (
										<AttachmentPreview
											key={attachment.id}
											attachment={attachment}
											onDownload={() => handleDownloadAttachment(attachment)}
											loading={isDownloading}
										/>
									))}
								{(isErrorAttachments ||
									(!isLoadingAttachments && attachments?.length === 0)) && (
									<p>Nenhum anexo encontrado.</p>
								)}
							</ul>
						</section>
					</div>
				</Layout>
			</Layout>
		</MainLayout>
	)
}
